// One chat turn (F-AGENT PR-B). User bubbles align to the inline-end (the
// lawyer's side), agent bubbles to the inline-start — flipped automatically
// in RTL because alignment is expressed with `items-start`/`items-end`
// (logical via flex + `dir`), never `text-left`/`ml-`/`right-`.
//
// Invariants:
//   §9 — `bidiProps(msg.text)` on EVERY bubble: a BE answer can be Arabic in
//        an FR chrome (and vice-versa), so direction is per-string, not
//        per-chrome. Logical radius (`rounded-s-*`/`rounded-e-*`) only.
//   W7 — chips render from `msg.chips` (BE-built descriptors) via
//        `CitationChips`; the bubble never parses `[REF:...]` to invent one.
//   W6 — no $/token/model text anywhere.
//   W2 — the only insert affordance is the DISABLED `InsertAffordance`.

import { useTranslation } from 'react-i18next'
import { bidiProps } from '@/lib/bidi'
import { CitationChips } from './CitationChips'
import { InsertAffordance } from './InsertAffordance'
import type { ChatMessage } from '@/domain/agent/entity'

interface MessageBubbleProps {
  message: ChatMessage
}

export function MessageBubble({ message }: MessageBubbleProps): JSX.Element {
  const { t } = useTranslation()
  const isUser = message.role === 'user'

  return (
    <div
      data-testid={`agent-msg-${message.role}`}
      data-status={message.status}
      className={
        'flex flex-col gap-1 ' + (isUser ? 'items-end' : 'items-start')
      }
    >
      <div
        className={bubbleClass(message)}
        {...bidiProps(message.text)}
      >
        {message.status === 'streaming' && (
          <span
            data-testid="agent-streaming-badge"
            className="mb-1 inline-block rounded-full bg-gold px-1.5 py-px text-[9px] font-bold uppercase tracking-wider text-ink"
          >
            {t('agent.bubble.streaming')}
          </span>
        )}

        {/* The accreting / final answer text. */}
        <span className="whitespace-pre-wrap">{message.text}</span>

        {/* Streaming caret affordance — a small pulsing block while live. */}
        {message.status === 'streaming' && (
          <span
            aria-hidden="true"
            data-testid="agent-streaming-caret"
            className="ms-0.5 inline-block h-3.5 w-1.5 animate-pulse bg-gold align-text-bottom"
          />
        )}

        {!isUser && <CitationChips chips={message.chips} />}
      </div>

      {/* Meta strip + (agent-only) the disabled Insérer affordance. */}
      <div className="flex items-center gap-2">
        <span className="text-[9px] text-ink/40">
          {isUser ? t('agent.bubble.you') : t('agent.bubble.agent')}
        </span>
        {message.status === 'error' && (
          <span
            data-testid="agent-msg-error-label"
            className="text-[9px] font-semibold text-warn"
          >
            {t('agent.bubble.errorLabel')}
          </span>
        )}
        {message.status === 'abstention' && (
          <span
            data-testid="agent-msg-abstention-label"
            className="text-[9px] font-semibold text-ink/50"
          >
            {t('agent.bubble.abstentionLabel')}
          </span>
        )}
        {!isUser && message.status !== 'streaming' && <InsertAffordance />}
      </div>
    </div>
  )
}

/** Bubble surface class by role + status. User = ink fill; agent = parchment
 *  card; error/abstention tint the agent card. Logical radius only. */
function bubbleClass(message: ChatMessage): string {
  const base =
    'max-w-[88%] rounded-2xl px-3 py-2 text-xs leading-relaxed rounded-e-sm'
  if (message.role === 'user') {
    return `${base} rounded-e-sm bg-ink text-white`
  }
  // agent — flip the small corner to the inline-start side.
  const agentBase = base.replace('rounded-e-sm', 'rounded-s-sm')
  if (message.status === 'error') {
    return `${agentBase} border border-warn/40 bg-warn/5 text-ink/80`
  }
  if (message.status === 'abstention') {
    return `${agentBase} border border-ink/15 bg-ink-soft/[0.04] text-ink/70`
  }
  if (message.status === 'streaming') {
    return `${agentBase} border border-gold bg-white text-ink/80`
  }
  return `${agentBase} border border-ink/10 bg-white text-ink/80`
}
